import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { postYoutubeSplit } from '../api/mediaApi';
import { useJobProgressStream } from '@/features/system/jobs/hooks/useJobProgressStream';
import JobProgressViewer from '@/features/system/jobs/components/JobProgressViewer';

interface YoutubeSplitPanelProps {
  autoClearDefault?: boolean;
}

const YoutubeSplitPanel: React.FC<YoutubeSplitPanelProps> = ({ autoClearDefault = true }) => {
  const [url, setUrl] = useState('');
  const [autoClear, setAutoClear] = useState(autoClearDefault);

  const mutation = useMutation({
    mutationFn: () => postYoutubeSplit({ url: url.trim() }),
    onSuccess: () => { if (autoClear) setUrl(''); },
  });

  const runId = (mutation.data as any)?.run_id || (mutation.data as any)?.jobId;
  const progress = useJobProgressStream(runId as any, { useSSE: true, fallbackPollMs: 4000 });
  const valid = /^https?:\/\/.+youtube\.com\//i.test(url.trim()) || /youtu\.be\//i.test(url.trim());

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-neutral-200">YouTube → Split</h2>
      <p className="text-sm text-neutral-400">Submit a YouTube URL.</p>
      <div className="flex flex-col gap-3">
        <input
          type="text"
          className="w-full rounded pac-glass-input text-sm text-neutral-100 outline-none focus:ring-indigo-500"
          placeholder="https://www.youtube.com/watch?v=..."
          value={url}
          onChange={e => setUrl(e.target.value)}
        />
        <div className="flex items-center gap-3">
          <button
            onClick={() => mutation.mutate()}
            disabled={!valid || mutation.isPending}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${(!valid || mutation.isPending) ? 'bg-neutral-700 text-neutral-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-500 text-white'}`}
          >{mutation.isPending ? 'Submitting…' : 'Run Split'}</button>
          <label className="flex items-center gap-2 text-xs text-neutral-400">
            <input type="checkbox" checked={autoClear} onChange={e=>setAutoClear(e.target.checked)} className="accent-indigo-600" />
            Clear field on success
          </label>
        </div>
        {!valid && url && (
          <p className="text-xs text-amber-500">Enter a valid YouTube URL.</p>
        )}
        {mutation.isError && (
          <p className="text-xs text-rose-500">Error: {(mutation.error as any)?.message || 'Request failed'}</p>
        )}
        {mutation.isSuccess && (
          <div className="text-xs space-y-2">
            <div className="text-emerald-400">Request accepted. Job ID: <span className="font-mono">{String(runId)}</span></div>
            <div className="rounded pac-glass-subtle p-3 space-y-2">
              <JobProgressViewer
                events={progress.events}
                latest={progress.latest}
                loading={progress.loading}
                error={progress.error}
                percent={progress.percent}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default YoutubeSplitPanel;
